'use client'

import {
  ColorBaseBlack,
  ColorBaseTransparent,
  ColorBaseWhite,
  ColorBrandPrimary,
  ColorDecorativePink70,
  ColorDecorativePurple70,
  ColorDecorativeYellow70,
  SizeRadiusS,
  SizeSpacing1,
  SizeSpacing3,
  SizeSpacing8,
} from '@snowball-tech/design-tokens/dist/web/typescript/design-tokens'
import {
  Paper,
  PaperElevations,
} from '@snowball-tech/fractal/dist/src/components/Paper'
import { cj } from '@snowball-tech/fractal/dist/src/styles/helpers'

import { type CSSProperties, useCallback, useState } from 'react'

import isEmpty from 'lodash/fp/isEmpty'
import isNumber from 'lodash/fp/isNumber'

import type { ChildrenProps } from '../../types'

import { ObjectFits } from '../../enums'
import { asPx, spacings } from '../../styles/helpers'
import ImageDisplay from '../Edition/ImageDisplay'
import LegacyTable from '../LegacyTable'

export const TYPE = 'image'

export const VARIANTS = [
  // '0',
  // PaperElevations.Bordered,
  PaperElevations.Elevated,
  // PaperElevations.Higher,
] as const
export type ImageVariant = (typeof VARIANTS)[number]
export const DEFAULT_VARIANT: ImageVariant = PaperElevations.Elevated

export const COLORS = [
  // 'blue',
  // 'green',
  'light-pink',
  'pink',
  'purple',
  'white',
  'yellow',
  'black',
  'transparent',
] as const
export type ImageColor = (typeof COLORS)[number]
export const DEFAULT_COLOR: ImageColor = 'white'

export const OBJECT_FITS = [ObjectFits.Contain, ObjectFits.Cover] as const
export type ImageFit = (typeof OBJECT_FITS)[number]
export const DEFAULT_OBJECT_FIT = ObjectFits.Contain

export const PADDINGS = ['none', 'small'] as const
export type ImagePadding = (typeof PADDINGS)[number]
export const DEFAULT_PADDING: ImagePadding = 'none'

export const DEFAULT_HEIGHT = 'auto'
export const DEFAULT_WIDTH = '100%'

type Props = {
  alt?: string
  color?: ImageColor
  fit?: ImageFit
  forHtml?: boolean
  height?: string
  id?: string
  padding?: ImagePadding
  url?: string
  variant?: ImageVariant
  width?: string
} & Partial<ChildrenProps>
export default function Image({
  alt = '',
  children: caption,
  color = DEFAULT_COLOR,
  fit = DEFAULT_OBJECT_FIT,
  forHtml = false,
  height = DEFAULT_HEIGHT,
  id,
  padding = DEFAULT_PADDING,
  url,
  variant = DEFAULT_VARIANT,
  width = DEFAULT_WIDTH,
}: Props) {
  const htmlId = isEmpty(id) ? undefined : `${TYPE}-${id}`

  const hasUrl = !isEmpty(url)

  const [isZoomed, setIsZoomed] = useState(false)
  const handleZoomIn = () => {
    if (!hasUrl) {
      return
    }

    setIsZoomed(true)
  }
  const exitZoom = useCallback(() => setIsZoomed(false), [setIsZoomed])

  if (!hasUrl) {
    return <div className="hidden" />
  }

  const actualVariant = variant || DEFAULT_VARIANT

  const image = (
    <ImageDisplay
      id={forHtml ? undefined : htmlId}
      alt={alt || ''}
      asImg={forHtml}
      exitZoom={forHtml ? undefined : exitZoom}
      height={
        isNumber(height) || !isEmpty(height)
          ? (asPx(height) as number)
          : undefined
      }
      inlineStyle={forHtml}
      objectFit={fit}
      size={{ height, width }}
      style={forHtml ? { height: 'auto' } : undefined}
      url={url}
      width={
        isNumber(width) || !isEmpty(width) ? (asPx(width) as number) : undefined
      }
      withLink={forHtml}
      withLoader={!forHtml}
      zoomed={forHtml ? false : isZoomed}
      onClick={forHtml ? undefined : handleZoomIn}
    />
  )

  const widthWithUnit = isNumber(asPx(width))
    ? asPx(width, { withUnit: true })
    : width

  const wrapperClassName = cj(
    'relative flex justify-center p-0 pointer-events-auto max-h-[unset] min-h-8 max-w-full align-center mx-auto',
    padding === 'small' ? 'p-1' : undefined,
  )
  const wrapperStyles: CSSProperties = {
    height: 'auto',
    maxHeight: height || undefined,
    maxWidth: '100%',
    width: `min(100%, ${widthWithUnit || DEFAULT_WIDTH})`,
    ...(forHtml
      ? {
          maxHeight: 'unset',
          minHeight: asPx(SizeSpacing8, { withUnit: true }),
          padding:
            padding === 'small' ? asPx(SizeSpacing1, { withUnit: true }) : 0,
          pointerEvents: 'auto',
        }
      : {}),
  }

  const actualColor = color || DEFAULT_COLOR
  let colorClassName = ''
  const colorStyle: CSSProperties = {}
  switch (actualColor) {
    case 'black': {
      colorClassName = 'bg-black'
      colorStyle.backgroundColor = ColorBaseBlack
      break
    }

    // case 'blue': {
    //   colorClassName = 'bg-decorative-blue-70'
    //   colorStyle.backgroundColor = ColorDecorativeBlue70
    //   break
    // }

    // case 'green': {
    //   colorClassName = 'bg-decorative-green-70'
    //   colorStyle.backgroundColor = ColorDecorativeGreen70
    //   break
    // }

    case 'light-pink': {
      colorClassName = 'bg-decorative-pink-70'
      colorStyle.backgroundColor = ColorDecorativePink70
      break
    }

    case 'pink': {
      colorClassName = 'bg-primary'
      colorStyle.backgroundColor = ColorBrandPrimary
      break
    }

    case 'purple': {
      colorClassName = 'bg-decorative-purple-70'
      colorStyle.backgroundColor = ColorDecorativePurple70
      break
    }

    case 'transparent': {
      colorClassName = 'bg-transparent'
      colorStyle.backgroundColor = ColorBaseTransparent
      break
    }

    case 'white': {
      colorClassName = 'bg-white'
      colorStyle.backgroundColor = ColorBaseWhite
      break
    }

    case 'yellow': {
      colorClassName = 'bg-decorative-yellow-70'
      colorStyle.backgroundColor = ColorDecorativeYellow70
      break
    }

    default: {
      colorClassName =
        (actualVariant as string) === '0' ? 'bg-transparent' : 'bg-white'
      colorStyle.backgroundColor =
        (actualVariant as string) === '0'
          ? ColorBaseTransparent
          : ColorBaseWhite
      break
    }
  }

  return forHtml ? (
    <LegacyTable
      id={htmlId}
      col
      data-color={actualColor}
      data-fit={fit}
      data-padding={padding}
      data-type={TYPE}
      data-url={url}
      data-width={width}
      defaultCellStyle={{ textAlign: 'center' }}
      gap={spacings[1]}
      style={{
        marginBottom: asPx(SizeSpacing3, { withUnit: true }),
      }}
    >
      <LegacyTable
        center
        col
        elevation={actualVariant}
        style={{
          borderRadius: SizeRadiusS,
          ...wrapperStyles,
          ...colorStyle,
        }}
      >
        {image}
      </LegacyTable>

      {caption}
    </LegacyTable>
  ) : (
    <div className="w-full">
      <div className="flex flex-col gap-1">
        {(actualVariant as string) === '0' ? (
          <div
            className={cj(wrapperClassName, colorClassName, 'rounded-sm')}
            style={wrapperStyles}
          >
            {image}
          </div>
        ) : (
          <Paper
            className={cj(wrapperClassName, colorClassName)}
            elevation={variant as '1' | '2' | '3'}
            style={wrapperStyles}
          >
            {image}
          </Paper>
        )}

        {caption}
      </div>
    </div>
  )
}
